import type { Novel } from '../data/samples';
import { CustomNovelsArraySchema } from './validation/storageSchemas';

const DB_NAME = 'novelflow_db';
const STORE_NAME = 'custom_novels';
const DB_VERSION = 1;

let dbInstance: IDBDatabase | null = null;
let initPromise: Promise<IDBDatabase> | null = null;

/**
 * IndexedDB を初期化する（シングルトン）
 */
const initDB = (): Promise<IDBDatabase> => {
  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }
  if (initPromise) {
    return initPromise;
  }

  initPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB is not supported in this environment.'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB open error:', event);
      initPromise = null;
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = (event) => {
      dbInstance = (event.target as IDBOpenDBRequest).result;

      dbInstance.onversionchange = () => {
        dbInstance?.close();
        dbInstance = null;
      };

      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });

  return initPromise;
};

/**
 * 保存されているすべてのカスタム小説を取得する
 */
export const getCustomNovels = async (): Promise<Novel[]> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const data = request.result;
        const result = CustomNovelsArraySchema.safeParse(data);
        if (result.success) {
          resolve(result.data as Novel[]);
        } else {
          console.warn('Failed to parse custom novels from IndexedDB:', result.error);
          resolve([]); // 不正なデータの場合は安全のため空配列を返すか、適切なエラー処理を行う
        }
      };

      request.onerror = () => {
        reject(new Error('Failed to get custom novels from IndexedDB'));
      };
    });
  } catch (error) {
    console.error('getCustomNovels error:', error);
    return [];
  }
};

/**
 * カスタム小説リストを保存する
 * 渡されたリストに存在しない小説IDは、IndexedDBから物理的に削除（容量解放）されます。
 */
export const saveCustomNovels = async (novels: Novel[]): Promise<void> => {
  try {
    const db = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      // DB上の現在のキー一覧を取得し、削除対象を特定する
      const getKeysRequest = store.getAllKeys();

      getKeysRequest.onsuccess = () => {
        const currentKeys = new Set(getKeysRequest.result as string[]);
        const newKeys = new Set(novels.map((n) => n.id));

        // 新しいリストに存在しないキーは削除する
        for (const key of currentKeys) {
          if (!newKeys.has(key)) {
            store.delete(key);
          }
        }

        // 残りの小説を追加・更新
        for (const novel of novels) {
          store.put(novel);
        }
      };

      getKeysRequest.onerror = () => {
        reject(new Error('Failed to fetch existing keys for diff update'));
      };

      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = (event) => {
        const error = transaction.error || (event.target as IDBTransaction)?.error;
        console.error('Transaction error during save:', error || event);
        if (error && (error.name === 'QuotaExceededError' || error.name === 'UnknownError')) {
          reject(
            new Error(
              'QuotaExceededError: ストレージ容量の上限を超過しました。不要な小説を削除してください。',
            ),
          );
        } else {
          reject(new Error(error?.message || 'Failed to save custom novels to IndexedDB'));
        }
      };
    });
  } catch (error) {
    console.error('saveCustomNovels error:', error);
    throw error;
  }
};

/**
 * PWA/ブラウザの永続ストレージを要求する
 */
export const requestPersistStorage = async (): Promise<boolean> => {
  if (typeof window === 'undefined' || !navigator.storage || !navigator.storage.persist) {
    return false;
  }

  try {
    const alreadyPersisted = await navigator.storage.persisted();
    if (alreadyPersisted) {
      console.log('Storage is already persisted');
      return true;
    }

    const isPersisted = await navigator.storage.persist();
    if (isPersisted) {
      console.log('Storage persist request approved');
    } else {
      console.warn('Storage persist request denied');
    }
    return isPersisted;
  } catch (error) {
    console.error('Error requesting storage persistence:', error);
    return false;
  }
};
